import dayjs from "dayjs";
import styles from "../MoveOutChecklist.module.css";
import { useEffect, useRef, useState } from "react";
import { Drawer } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { X } from "react-feather";
import SignatureCanvas from "react-signature-canvas";
import { PhaseBanner } from "./CommonComponents";

const StatementTab = ({ leaseData, moveoutData, submitMoveoutChceckList, propertyDetails, setLoading }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [paymentData, setPaymentData] = useState(null);

    useEffect(() => {
        const fetchProperty = async () => {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            await axios.get(process.env.REACT_APP_PAYMENT_API_URL + "v1/moveout-payment-data/" + leaseData?.tenant_id, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            }).then((response) => {
                setPaymentData(response.data.data);
            }).catch((error) => { });
        }
        if (leaseData?.tenant_id) {
            fetchProperty();
        }
    }, [leaseData?.tenant_id]);

    const [comments, setComments] = useState("");
    const [managerSignature, setManagerSignature] = useState(null);
    const [tenant1Signature, setTenant1Signature] = useState(null);
    const [tenant2Signature, setTenant2Signature] = useState(null);
    const [tenant3Signature, setTenant3Signature] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [currentSigner, setCurrentSigner] = useState(null);
    const signaturePadRef = useRef();

    const totalBalance = parseFloat(leaseData?.security_amount) + parseFloat(paymentData?.paid_amount) - parseFloat(paymentData?.prorated_rent) - parseFloat(paymentData?.prev_balance) - parseFloat(moveoutData?.damage_cost);

    const onSubmit = () => {
        let data = { comments: comments, 'is_submitted': 1, 'is_completed': 1, current_step: 5, final_amount: totalBalance };
        if (managerSignature) {
            data = { ...data, manager_sign: managerSignature?.file_path };
        }
        if (tenant1Signature) {
            data = { ...data, tenant1_sign: tenant1Signature?.file_path };
        }
        if (tenant2Signature) {
            data = { ...data, tenant2_sign: tenant2Signature?.file_path };
        }
        if (tenant3Signature) {
            data = { ...data, tenant3_sign: tenant3Signature?.file_path };
        }

        submitMoveoutChceckList(data);
    }



    const submitSignature = async (base64Data, name) => {
        const file = base64Data;

        setLoading(true);
        try {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            const formData = new FormData();
            formData.append("image", file);

            const response = await axios.post(process.env.REACT_APP_PAYMENT_API_URL + `v1/save-sign-image-move-out`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                }
            });

            signaturePadRef.current.clear();

            switch (name) {
                case "tenant1":
                    setTenant1Signature(response.data.data);
                    break;
                case "tenant2":
                    setTenant2Signature(response.data.data);
                    break;
                case "tenant3":
                    setTenant3Signature(response.data.data);
                    break;
                case "manager":
                    setManagerSignature(response.data.data);
                    break;
                default:
                    console.log('Not Match');
            }

        } catch (error) {
            console.error("Signature Upload error:", error);
            toast.error(error?.response?.data?.message || "Signature upload failed");
        } finally {
            setLoading(false);
        }
    }

    const buttonDisabled = !managerSignature || (moveoutData?.tenant1_name && !tenant1Signature) || (moveoutData?.tenant2_name && !tenant2Signature) || (moveoutData?.tenant3_name && !tenant3Signature);

    return <>
        <div className={`${styles.tp} ${styles.tpActive}`}>
            <PhaseBanner
                type="green"
                icon="receipt"
                title="Final Statement — Ready to send"
                sub={`Inspection on ${dayjs(moveoutData?.inspection_date).format("MMM DD, YYYY")}. Sign below and send the final statement to issue ${totalBalance > 0 ? "a refund" : "a charge"} to the tenant.`}
            />

            {/* Rent adjustment */}
            <div className={styles.sc}>
                <div className={styles.scTitle}>
                    Rent adjustment — {dayjs(moveoutData?.moveout_date).format("MMM YYYY")}
                </div>
                <div className={styles.sr}><span className={styles.sk}>Move-out date</span><span className={styles.sv}>{dayjs(moveoutData?.moveout_date).format("MMM DD, YYYY")}</span></div>
                <div className={styles.sr}><span className={styles.sk}>Inspection date</span><span className={styles.sv}>{dayjs(moveoutData?.inspection_date).format("MMM DD, YYYY")}</span></div>
                <div className={styles.sr}><span className={styles.sk}>Days occupied in month</span><span className={styles.sv}>{dayjs(moveoutData?.moveout_date).date()} of {dayjs(moveoutData?.moveout_date).daysInMonth()} days</span></div>
                {paymentData && paymentData?.bill_amount > 0 && <div className={styles.sr}><span className={styles.sk}>Monthly Bill</span><span className={styles.sv}>{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(paymentData?.bill_amount)}</span></div>}
                {paymentData && paymentData?.prorated_rent > 0 && paymentData?.days !== paymentData?.occupied_days && <div className={styles.sr}><span className={styles.sk}>{"Pro-rated rent for " + dayjs(moveoutData?.moveout_date).date() + " days"}</span><span className={styles.sv}>{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(paymentData?.prorated_rent)}</span></div>}
                {paymentData && paymentData?.paid_amount > 0 && <div className={styles.sr}><span className={styles.sk}>Paid</span><span className={styles.sv}>{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(paymentData?.paid_amount)}</span></div>}
                {paymentData && (paymentData?.prorated_rent > paymentData?.paid_amount) && <div className={styles.sr}><span className={styles.sk}>Due of {dayjs(moveoutData?.moveout_date).format('MMM YYYY')}</span><span className={`${styles.sv} ${styles.charge}`}>-{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((paymentData?.prorated_rent - paymentData?.paid_amount))}</span></div>}
                {paymentData && (paymentData?.prorated_rent <= paymentData?.paid_amount) && <div className={styles.sr}><span className={styles.sk}>Credit for {dayjs(moveoutData?.moveout_date).format('MMM YYYY')}</span><span className={`${styles.sv} ${styles.credit}`}>+{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((paymentData?.paid_amount - paymentData?.prorated_rent))}</span></div>}
            </div>

            {/* Deposit settlement */}
            <div className={styles.sc}>
                <div className={styles.scTitle}>Deposit settlement</div>
                <div className={styles.sr}><span className={styles.sk}>Security deposit held</span><span className={styles.sv}>{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((leaseData?.security_amount))}</span></div>
                {paymentData && (paymentData?.prorated_rent <= paymentData?.paid_amount) && <div className={styles.sr}><span className={styles.sk}>Rent credit</span><span className={`${styles.sv} ${styles.credit}`}>+{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((paymentData?.paid_amount - paymentData?.prorated_rent))}</span></div>}
                {paymentData && (paymentData?.prorated_rent > paymentData?.paid_amount) && <div className={styles.sr}><span className={styles.sk}>Rent charge</span><span className={`${styles.sv} ${styles.charge}`}>-{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((paymentData?.prorated_rent - paymentData?.paid_amount))}</span></div>}
                <div className={styles.sr}><span className={styles.sk}>Previous Outstanding Balance</span><span className={`${styles.sv} ${styles.charge}`}>-{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((paymentData?.prev_balance))}</span></div>
                <div className={styles.sr}><span className={styles.sk}>Damage charges (tenant)</span><span className={`${styles.sv} ${moveoutData?.damage_cost > 0 ? styles.charge : ""}`}>-{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((moveoutData?.damage_cost))}</span></div>

                {/*<div className={styles.sr}>
                <span className={styles.sk}>Voided Invoice</span>
                <span className={`${styles.sv} ${styles.credit}`}>
                    June 2026
                </span>
            </div>*/}

                <div className={styles.sr}>
                    <span className={styles.sk}>{totalBalance > 0 ? "Refund to tenant" : "Balance due from tenant"}</span>
                    <span className={`${styles.sv} ${totalBalance > 0 ? styles.credit : styles.charge}`} style={{ fontSize: 16 }}>
                        {totalBalance > 0 ? "" : "-"}{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((totalBalance))}
                    </span>
                </div>
            </div>

            {/* Damage detail */}
            {moveoutData?.damages.length > 0 && (
                <div className={styles.sc}>
                    <div className={styles.scTitle}>Damage detail</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: `1px solid #E5E7EB` }}>
                                <th style={{ textAlign: "left", padding: "6px 0", fontSize: 11, fontWeight: 600, color: "#6B7280" }}>Item</th>
                                <th style={{ textAlign: "right", padding: "6px 0", fontSize: 11, fontWeight: 600, color: "#6B7280" }}>Cost</th>
                            </tr>
                        </thead>
                        <tbody>
                            {moveoutData?.damages.map((d, i) => (
                                <tr key={i} style={{ borderBottom: "1px solid #E5E7EB" }}>
                                    <td style={{ padding: "7px 0" }}>{d?.description || "—"}</td>
                                    <td style={{ padding: "7px 0", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>${(d?.cost || 0).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Verdict */}
            <div className={`${styles.verdict} ${totalBalance > 0 ? styles.verdictRefund : styles.verdictOwe}`}>
                <i className={`ti ${totalBalance > 0 ? "ti-circle-check" : "ti-alert-circle"} ${styles.verdictIcon}`} />
                <div>
                    <div className={styles.vt}>
                        {totalBalance > 0 ? `Refund of ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((totalBalance))}` : `Collect balance of ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((totalBalance))}`}
                    </div>
                    <div className={styles.vs}>
                        {totalBalance > 0 ? "Post credit to tenant ledger. Transfer refund within 30 days of move-out." : "Raise final invoice. Collect before releasing deposit remainder."}
                    </div>
                </div>
            </div>

            {/* Comments */}
            <div style={{ marginBottom: 16 }}>
                <label className={styles.fl}>Comments</label>
                <div className={styles.cmts}>
                    <textarea placeholder="Comments" onChange={(e) => setComments(e.target.value)} />
                </div>
            </div>

            {/* Signatures */}
            <div className={styles.sigSec}>
                <div className={styles.sigSecTitle}>Move-out Inspection</div>
                <div className={styles.sigBlock}>
                    {managerSignature ? <img src={managerSignature?.file_url} alt="signature" className={`${styles.SignatureImage}`} /> : <button className={`${styles.sigBtn}`} onClick={() => { setCurrentSigner('manager'); setDrawerOpen(true); }}>SIGNATURE</button>}
                    <div className={styles.sigName}>{moveoutData?.signed_by_name}</div>
                    <div className={styles.sigMeta}>Authorized Agent - {propertyDetails?.property_manager}</div>
                </div>
                {moveoutData?.tenant1_name && <div className={styles.sigBlock}>
                    {tenant1Signature ? <img src={tenant1Signature?.file_url} alt="signature" className={`${styles.SignatureImage}`} /> : <button className={`${styles.sigBtn}`} onClick={() => { setCurrentSigner('tenant1'); setDrawerOpen(true); }}>SIGNATURE</button>}
                    <div className={styles.sigName}>{moveoutData?.tenant1_name}</div>
                    <div className={styles.sigMeta}>Tenant</div>
                </div>}
                {moveoutData?.tenant2_name && <div className={styles.sigBlock}>
                    {tenant2Signature ? <img src={tenant2Signature?.file_url} alt="signature" className={`${styles.SignatureImage}`} /> : <button className={`${styles.sigBtn}`} onClick={() => { setCurrentSigner('tenant2'); setDrawerOpen(true); }}>SIGNATURE</button>}
                    <div className={styles.sigName}>{moveoutData?.tenant2_name}</div>
                    <div className={styles.sigMeta}>Tenant</div>
                </div>}
                {moveoutData?.tenant3_name && <div className={styles.sigBlock}>
                    {tenant3Signature ? <img src={tenant3Signature?.file_url} alt="signature" className={`${styles.SignatureImage}`} /> : <button className={`${styles.sigBtn}`} onClick={() => { setCurrentSigner('tenant3'); setDrawerOpen(true); }}>SIGNATURE</button>}
                    <div className={styles.sigName}>{moveoutData?.tenant3_name}</div>
                    <div className={styles.sigMeta}>Tenant</div>
                </div>}
                <div className={styles.sigDate}>
                    Date of Move-out Inspection <strong>{dayjs(moveoutData?.inspection_date).format("MMM DD, YYYY")}</strong>
                </div>
            </div>

            <div className={styles.fa}>
                <button className={`${styles.btn} ${styles.btnS}`} onClick={() => { }}>
                    <i className="ti ti-arrow-left" /> Back
                </button>
                <div className={styles.faRight}>
                    <button className={`${styles.btn} ${styles.btnS}`}>Cancel</button>
                    <button
                        className={`${styles.btn} ${styles.btnP}`}
                        onClick={onSubmit}
                        disabled={buttonDisabled}
                    >
                        <i className="ti ti-send" /> Send final statement
                    </button>
                </div>
            </div>
        </div >

        <Drawer
            anchor="bottom"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            className="ChecklistSignDrawer"
            ModalProps={{
                keepMounted: true, // improves performance on mobile
            }}
        >
            <div className={`${styles.DrawerContent}`}>
                <div className={`${styles.SignDrawerHead}`}>
                    <p className={`${styles.DrawerSigTitle}`}>Sign as {currentSigner === 'manager' ? 'Manager' : 'Tenant'}</p>
                    <button onClick={() => setDrawerOpen(false)} className={`${styles.CloseButton}`} aria-label="Close"><X /></button>
                </div>

                <SignatureCanvas
                    penColor="black"
                    canvasProps={{
                        width: 330,
                        height: 180,
                        className: `${styles.SignatureCanvas}`,
                    }}
                    ref={signaturePadRef}
                />
                <div className={`${styles.ButtonAction}`}>
                    <button onClick={() => signaturePadRef.current.clear()} className={`${styles.CancelPopupBU}`}>Clear</button>
                    <button className={`${styles.SubmitPopupBU}`} variant="contained" onClick={() => {
                        if (signaturePadRef.current.isEmpty()) {
                            toast.error("Signature cannot be empty. Please provide a valid signature before submitting.");
                            return;
                        }
                        const base64 = signaturePadRef.current.toDataURL();
                        submitSignature(base64, currentSigner);
                        setDrawerOpen(false);
                    }}>Ok</button>
                </div>
            </div>
        </Drawer>
    </>
}

export default StatementTab;